import React, { useContext, useEffect, useState } from 'react';
import { Drawer, Skeleton, Spin } from 'antd';
import { Device, MainContext } from '../../../../contexts/mainContext';
import {
  EditServiceMutationVariables,
  ServiceQuery,
  useCreateServiceMutation,
  useEditServiceMutation,
  useGetAllClientsQuery,
  useGetAllDriversQuery,
  useGetAllDrivingCompaniesQuery,
  useGetAllVehiclesQuery,
  useGetPassengersQuery,
  useServiceQuery,
  useServiceTypesQuery,
} from '../../../../graphql/datamodel.gen';
import { getClient } from '../../../../apollo';
import { ICommonProps } from './common';
import { MyEditServiceForm } from './EditServiceForm';
import { NetworkStatus } from '@apollo/client';

interface ICreateServiceDrawerProps {
  visible: boolean;
  serviceId: string;
  volcanoServiceId: string;
  onClose: () => void;
}

const EditServiceDrawer: React.FC<ICreateServiceDrawerProps> = (
  props: ICreateServiceDrawerProps,
) => {
  const mainContext = useContext(MainContext);

  const {
    loading: loadingService,
    data: dataService,
    error: errorService,
    refetch: refetchService,
    networkStatus: networkStatusService,
  } = useServiceQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: props.serviceId,
    },
  });

  const {
    loading: loadingClients,
    data: dataClients,
    error: errorClients,
    networkStatus: networkStatusClients,
    refetch: refetchClients,
  } = useGetAllClientsQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingDrivers,
    data: dataDrivers,
    error: errorDrivers,
    networkStatus: networkStatusDrivers,
    refetch: refetchDrivers,
  } = useGetAllDriversQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      fetchDrivingCompany: false,
    },
  });

  const {
    loading: loadingDrivingCompanies,
    data: dataDrivingCompanies,
    error: errorDrivingCompanies,
    networkStatus: networkStatusDrivingCompanies,
    refetch: refetchDrivingCompanies,
  } = useGetAllDrivingCompaniesQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingServiceTypes,
    data: dataServiceTypes,
    error: errorServiceTypes,
    networkStatus: networkStatusServiceTypes,
  } = useServiceTypesQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingPassengers,
    data: dataPassengers,
    error: errorPassengers,
    refetch: refetchPassengers,
    networkStatus: networkStatusPassengers,
  } = useGetPassengersQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingVehicles,
    data: dataVehicles,
    error: errorVehicles,
    networkStatus: networkStatusVehicles,
    refetch: refetchVehicles,
  } = useGetAllVehiclesQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [createService] = useCreateServiceMutation({
    client: getClient(),
  });

  const [editService] = useEditServiceMutation({
    client: getClient(),
  });

  const drivers = dataDrivers?.drivers || [];
  const clients = dataClients?.clients || [];
  const serviceTypes = dataServiceTypes?.serviceTypes || [];
  const passengers = dataPassengers?.passengers || [];
  const vehicles = dataVehicles?.vehicles || [];
  const service = dataService?.service;
  const drivingCompanies = dataDrivingCompanies?.drivingCompanies || [];

  const [selectedPassengers, setSelectedPassengers] = useState([]);

  const _loadingClients =
    (loadingClients && !dataClients) ||
    networkStatusClients === NetworkStatus.refetch;

  const _loadingDrivers =
    (loadingDrivers && !dataDrivers) ||
    networkStatusDrivers === NetworkStatus.refetch;

  const _loadingPassengers =
    (loadingPassengers && !dataPassengers) ||
    networkStatusPassengers === NetworkStatus.refetch;

  const _loadingServiceTypes =
    (loadingServiceTypes && !dataServiceTypes) ||
    networkStatusServiceTypes === NetworkStatus.refetch;

  const _loadingVehicles =
    (loadingVehicles && !dataVehicles) ||
    networkStatusVehicles === NetworkStatus.refetch;

  const _loadingService =
    loadingService || networkStatusService === NetworkStatus.refetch;

  const _loadingDrivingCompanies =
    (loadingDrivingCompanies && !dataDrivingCompanies) ||
    networkStatusDrivingCompanies === NetworkStatus.refetch;

  const loading =
    _loadingClients ||
    _loadingDrivers ||
    _loadingPassengers ||
    _loadingServiceTypes ||
    _loadingVehicles ||
    _loadingService ||
    _loadingDrivingCompanies;

  useEffect(() => {
    setSelectedPassengers([
      ...(service?.passengers || []).map((passenger) => passenger.id),
    ]);
  }, [service]);

  const { device } = mainContext;

  const _updateService = async (
    serviceCreateInput: EditServiceMutationVariables,
  ): Promise<boolean> => {
    try {
      const response = await editService({
        variables: {
          ...serviceCreateInput,
        },
      });

      refetchPassengers();
      refetchService();
      refetchDrivers();

      return !!response.data?.editService;
    } catch (e) {
      console.error('Error while trying to update a service', e);
      return false;
    }
  };

  const _refetchPassengers = async (newPassengerId: string) => {
    await refetchPassengers();
    setSelectedPassengers([...selectedPassengers, newPassengerId]);
  };

  return (
    <EditServiceDrawerView
      {...props}
      drivingCompanies={drivingCompanies}
      isAdmin={mainContext.isAdmin}
      isClient={mainContext.isClient}
      close={props.onClose}
      service={service}
      editService={_updateService}
      loading={loading}
      device={device}
      clients={clients}
      drivers={drivers}
      vehicles={vehicles}
      serviceTypes={serviceTypes}
      passengers={passengers}
      selectedPassengers={selectedPassengers}
      refetchPassengers={_refetchPassengers}
      refetchService={refetchService}
    />
  );
};

interface IEditServiceDrawerViewProps extends ICommonProps {
  visible: boolean;
  loading: boolean;
  device: Device;
  isAdmin: boolean;
  isClient: boolean;
  service: ServiceQuery['service'];
  onClose: (...args: any[]) => any;
}

interface IEditServiceDrawerViewState {}

class EditServiceDrawerView extends React.Component<
  IEditServiceDrawerViewProps,
  IEditServiceDrawerViewState
> {
  render(): React.ReactNode {
    let width: string = '50%';

    if (this.props.device.type === 'desktop' && this.props.device.bigScreen) {
      width = '30%';
    }
    if (this.props.device.type === 'desktop' && !this.props.device.bigScreen) {
      width = '75%';
    }
    if (this.props.device.type === 'phone') {
      if (this.props.device.bigScreen) {
        width = '75%';
      } else {
        width = '100%';
      }
    }

    return (
      <Drawer
        title={`Gestionar servicio ${
          this.props.volcanoServiceId || this.props.serviceId
        }`}
        onClose={this.props.onClose}
        closable={true}
        maskClosable={true}
        destroyOnClose={true}
        mask={true}
        width={width}
        visible={this.props.visible}
      >
        {(this.props.loading ||
          !this.props.service ||
          !this.props.service?.id) && (
          <Skeleton active={true} paragraph={{ rows: 10 }} />
        )}
        {!this.props.loading &&
          this.props.service &&
          this.props.service?.id && <MyEditServiceForm {...this.props} />}
      </Drawer>
    );
  }
}

export default EditServiceDrawer;
